// Pure text/view-model rules for the agent home screen (specs/ai-agent-home-redesign).
// Kept out of the component so the children stay presentational and the rules are unit-testable.

export interface AgentScope {
  networkName: string;
  groupsLabel: string;
  groupName: string;
}

export interface QuickAction {
  icon: string;
  label: string;
  sublabel: string;
  message: string;
}

export interface ReasonContext {
  groups: any[];
  savedGroupIds: number[];
  savedPreferenceNames: string[];
  currentGroupSlug: string;
  currentGroupName: string;
}

const lower = (value: any): string => String(value ?? '').trim().toLowerCase();

export function getWelcomeSubtitle(scope: AgentScope): string {
  if (scope.groupName) {
    return `Your ${scope.groupName} fan experience, connected to the ${scope.networkName || 'fan'} network.`;
  }
  return 'A better way to follow every game, team and moment you care about.';
}

export function getRecommendedSubtitle(scope: AgentScope, savedPreferenceNames: string[]): string {
  const names = (savedPreferenceNames || []).filter(Boolean);
  if (names.length) return `Based on ${names.slice(0, 3).join(' · ')}. Refine what matters to you.`;
  if (scope.groupName) return `Personalized to ${scope.groupName} and your saved interests.`;
  return `Personalized to your ${scope.networkName || 'fan'} interests.`;
}

export function getAskContext(scope: AgentScope): string {
  if (scope.groupName) return scope.groupName;
  return ['all', scope.networkName, lower(scope.groupsLabel)].filter(Boolean).join(' ');
}

export function buildQuickActions(scope: AgentScope): QuickAction[] {
  return [
    { icon: 'event', label: 'Find events', sublabel: `Across ${scope.networkName || 'the network'}`, message: 'What events are coming up?' },
    { icon: 'play_arrow', label: 'Watch games', sublabel: 'Live & replay', message: "What's streaming live right now?" },
    { icon: 'shopping_bag', label: 'Shop merch', sublabel: `Your ${lower(scope.groupsLabel) || 'teams'}`, message: 'Browse Merchandise' },
    { icon: 'groups', label: 'Join fans', sublabel: 'Communities', message: 'Find communities to join' },
  ];
}

// First matching rule wins; '' means "no reason", and the card hides the line.
export function getRecommendationReason(item: any, ctx: ReasonContext): string {
  if (!item) return '';
  const groupId = Number(item.networkGroupId);
  if (groupId && (ctx.savedGroupIds || []).includes(groupId)) {
    const group = (ctx.groups || []).find((g: any) => Number(g.group_id || g.id) === groupId);
    const name = group?.group_name || group?.name;
    if (name) return `Because you follow ${name}`;
  }

  const saved = (ctx.savedPreferenceNames || []).map(lower).filter(Boolean);
  // Saved sports read "Basketball" on load but "Basketball (Men's)" right after a save.
  const sport = lower(item.sportName);
  if (sport && saved.some(name => name === sport || name.startsWith(sport + ' ('))) {
    return `Because you follow ${item.sportName}`;
  }

  const genre = (item.genres || []).find((g: any) => saved.includes(lower(g)));
  if (genre) return `Based on your interest in ${genre}`;

  if (ctx.currentGroupSlug && item.groupSlug === ctx.currentGroupSlug && ctx.currentGroupName) {
    return `Because you are viewing ${ctx.currentGroupName}`;
  }
  return '';
}

// "All" plus the first 2 groups; a selected group outside those 2 takes the second slot so it
// is always visible as selected.
export function getMemberBarGroups(groups: any[], currentGroupSlug: string): any[] {
  const list = groups || [];
  const firstTwo = list.slice(0, 2);
  if (!currentGroupSlug || firstTwo.some(g => g.group_slug === currentGroupSlug)) return firstTwo;
  const selected = list.find(g => g.group_slug === currentGroupSlug);
  return selected ? [firstTwo[0], selected].filter(Boolean) : firstTwo;
}
